// =====================================================
// Pays sélectionnables — chacun apporte un modificateur passif.
// Activé uniquement si la partie est créée avec useCountries: true.
// =====================================================

import type { CardCategory } from "../types.js";

/**
 * Type des modificateurs supportés par le moteur.
 *
 * - "starting_hexes"        : démarre avec N hex en densité D
 * - "category_buff"         : buff continu sur une catégorie (deltaDensity? / deltaSpread? / deltaFailRate? / deltaDuration?)
 * - "intel_extend"          : prolonge la durée des renseignements reçus (extraTurns)
 * - "no_expose_loss"        : ignore la perte d'hex pendant Exposé (limité à `maxPerRound` × par round)
 * - "extra_draft"           : pool de draft élargi au round 1 (extra)
 * - "round_shield"          : bouclier consommable qui annule la première attaque entrante de chaque round
 * - "charge_shorten"        : cartes en chargement durent N tours de moins (delta)
 * - "see_seat_neighbor"     : voit le territoire des voisins de siège sans intel
 */
export type CountryModifierType =
  | "starting_hexes"
  | "category_buff"
  | "intel_extend"
  | "no_expose_loss"
  | "extra_draft"
  | "round_shield"
  | "charge_shorten"
  | "see_seat_neighbor"
  | "play_category_bonus";

export interface CountryModifier {
  type: CountryModifierType;
  // Données optionnelles selon le type
  category?: CardCategory;
  deltaDensity?: number;
  deltaSpread?: number;
  deltaFailRate?: number;
  deltaDuration?: number;
  count?: number;
  density?: number;
  extraTurns?: number;
  extra?: number;
  delta?: number;
  /** Limite par round pour les effets consommables (round_shield, no_expose_loss). */
  maxPerRound?: number;
}

export interface Country {
  id: string;
  /** Nom court affiché dans la liste et les chips. */
  name: string;
  /** Nom complet pour la signature des messages diplomatiques. */
  fullName: string;
  /** Devise / phrase d'ambiance pour le briefing. */
  motto: string;
  /** Texte humain expliquant le modificateur (affiché au joueur). */
  modifierLabel: string;
  /** Modificateur appliqué par le moteur (ou null si v2). */
  modifier: CountryModifier | null;
}

export const countriesData: Country[] = [
  {
    id: "methane",
    name: "République du Méthane",
    fullName: "République Démocratique du Méthane",
    motto: "Le sous-sol nous appartient, les nuages aussi.",
    modifierLabel: "Industrie polluante — vos cartes Densité gagnent +1 étendue (densifient un hex de plus, débordement fréquent).",
    modifier: { type: "category_buff", category: "density", deltaSpread: 1 },
  },
  {
    id: "emirats",
    name: "Émirats Aérés Unis",
    fullName: "Fédération des Émirats Aérés Unis",
    motto: "Climatisation centralisée depuis 1973.",
    modifierLabel: "Confort tempéré — vos cartes Défense durent 1 tour de plus.",
    modifier: { type: "category_buff", category: "defense", deltaDuration: 1 },
  },
  {
    id: "vent_continu",
    name: "Confédération du Vent Continu",
    fullName: "Confédération Helvetico-Aérienne du Vent Continu",
    motto: "On ne s'arrête jamais. Au sens propre.",
    modifierLabel: "Régularité institutionnelle — vos cartes Densité gagnent +1 densité.",
    modifier: { type: "category_buff", category: "density", deltaDensity: 1 },
  },
  {
    id: "souffle_sur_mer",
    name: "Principauté de Souffle-sur-Mer",
    fullName: "Sérénissime Principauté de Souffle-sur-Mer",
    motto: "La brise marine emporte tout. Surtout les preuves.",
    modifierLabel: "Brise persistante — la première attaque entrante de chaque round est annulée.",
    modifier: { type: "round_shield", maxPerRound: 1 },
  },
  {
    id: "flatus",
    name: "Royaume du Flatus",
    fullName: "Royaume Constitutionnel du Flatus",
    motto: "Honneur, tradition, méthane.",
    modifierLabel: "Tradition flatueuse — vous démarrez avec 1 hex supplémentaire en densité 2.",
    modifier: { type: "starting_hexes", count: 1, density: 2 },
  },
  {
    id: "brouillards",
    name: "Commonwealth des Brouillards Légers",
    fullName: "Commonwealth Britannique des Brouillards Légers",
    motto: "We don't comment on ongoing emissions.",
    modifierLabel: "Discrétion impériale — vos cartes en chargement durent 1 tour de moins, et vos cartes Sabotage durent 1 tour de plus.",
    // Effet combiné : charge_shorten via type principal (-1 tour sur cartes en
    // chargement), ET un buff sabotage via tag secondaire (+1 duration) lu par
    // getCountryCategoryBuff. Concrètement : Confinement passe de 1 → 2 tours.
    modifier: { type: "charge_shorten", delta: 1, category: "sabotage", deltaDuration: 1 },
  },
  {
    id: "soufflant_levant",
    name: "Empire du Soufflant Levant",
    fullName: "Empire Éternel du Soufflant Levant",
    motto: "Le vent souffle d'où nous le décrétons.",
    modifierLabel: "Vents dominants — vos cartes Risque ont -15% d'échec.",
    modifier: { type: "category_buff", category: "risk", deltaFailRate: -0.15 },
  },
  {
    id: "nordique",
    name: "Fédération Nordique des Émissions",
    fullName: "Fédération Nordique Coordonnée des Émissions",
    motto: "Transparence totale. Sauf nous.",
    modifierLabel: "Réseau scandinave — vous voyez en permanence le territoire de vos voisins de siège.",
    modifier: { type: "see_seat_neighbor" },
  },
  {
    id: "bananiere",
    name: "République Bananière Méthanogène",
    fullName: "République Bananière Insulaire Méthanogène",
    motto: "Nos stocks sont stratégiques. Le reste est négociable.",
    modifierLabel: "Stocks abondants — pool de draft +2 cartes au round 1.",
    modifier: { type: "extra_draft", extra: 2 },
  },
  {
    id: "hilarants",
    name: "États Hilarants d'Outre-Manche",
    fullName: "Confédération des États Hilarants d'Outre-Manche",
    motto: "On rit. Quelqu'un doit bien le faire.",
    modifierLabel: "Diplomatie sarcastique — la première perte d'hex de chaque round est annulée.",
    modifier: { type: "no_expose_loss", maxPerRound: 1 },
  },
  {
    id: "khanat",
    name: "Khanat des Steppes Aérées",
    fullName: "Grand Khanat des Steppes Aérées Mongoles",
    motto: "Vaste. Très vaste. Trop, peut-être.",
    modifierLabel: "Mobilité nomade — chaque carte Sabotage que vous jouez vous offre 1 hex D1 bonus (les espions s'installent en territoire conquis).",
    modifier: { type: "play_category_bonus", category: "sabotage", count: 1, density: 1 },
  },
  {
    id: "vapeurs",
    name: "Sérénissime de Vapeurs",
    fullName: "Sérénissime République de Vapeurs",
    motto: "Espionnage depuis 1453, sans interruption.",
    modifierLabel: "Réseau d'information ancestral — vos renseignements durent +2 tours.",
    modifier: { type: "intel_extend", extraTurns: 2 },
  },
];

export function findCountry(id: string | undefined): Country | undefined {
  if (!id) return undefined;
  return countriesData.find((c) => c.id === id);
}
